import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ArrowLeft, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ConversationDetailPage = () => {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [threadInfo, setThreadInfo] = useState(null);

  useEffect(() => {
    fetchConversation();
  }, [threadId]);

  const fetchConversation = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/messages/${threadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.messages);
      setThreadInfo(response.data.thread_info);
    } catch (error) {
      toast.error('Failed to load conversation');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#62D84E' }}></div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <Button variant="ghost" onClick={() => navigate('/admin/messages')} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Messaging
        </Button>
        <h1 className="text-3xl font-bold" style={{ color: '#1B3240' }}>Conversation Details</h1>
        <p className="text-gray-600 mt-2">View message thread between companies</p>
      </div>

      {/* Thread Info */}
      {threadInfo && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Thread Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-gray-600 text-sm">Thread ID</p>
                <p className="font-mono text-sm">{threadId.substring(0, 8)}...</p>
              </div>
              <div>
                <p className="text-gray-600 text-sm">Subject</p>
                <p className="font-medium">{threadInfo.subject || 'N/A'}</p>
              </div>
              <div>
                <p className="text-gray-600 text-sm">From Company</p>
                <p className="font-medium">{threadInfo.from_company?.name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-gray-600 text-sm">To Company</p>
                <p className="font-medium">{threadInfo.to_company?.name || 'N/A'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Messages */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <MessageSquare className="w-5 h-5 mr-2" />
            Messages ({messages.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {messages.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No messages in this conversation</p>
          ) : (
            <div className="space-y-4">
              {messages.map((item, idx) => {
                const msg = item.message;
                const fromCompany = item.from_company;
                const toCompany = item.to_company;
                return (
                  <div
                    key={msg.id || idx}
                    className="p-4 rounded-lg bg-gray-50 border border-gray-200"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium text-sm">
                          {fromCompany?.name || 'Unknown Company'}
                        </p>
                        <p className="text-xs text-gray-500">
                          To: {toCompany?.name || 'Unknown'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {msg.created_at ? new Date(msg.created_at).toLocaleString() : 'N/A'}
                        </p>
                      </div>
                      {msg.message_type === 'system' && (
                        <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                          System Message
                        </span>
                      )}
                    </div>
                    {msg.subject && (
                      <p className="font-semibold text-sm mb-2">{msg.subject}</p>
                    )}
                    <div
                      className="text-sm text-gray-700"
                      dangerouslySetInnerHTML={{ __html: msg.content }}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ConversationDetailPage;